/* eslint-disable react-hooks/exhaustive-deps */
import { MENU_COLORS } from "@lhd/constants";
import { FormInstance } from "antd";
import { isEqual, get } from "lodash";
import { useEffect, useRef, useState } from "react";

type FieldState = "initial" | "normal" | "error";

// Helper to convert field name to string key
const getFieldKey = (name: string | string[]): string => {
  return Array.isArray(name) ? name.join(".") : name;
};

// Helper to get nested value from object
const getFieldValue = (values: any, name: string | string[]): any => {
  if (Array.isArray(name)) {
    return get(values, name);
  }
  return values[name];
};

export const useSubmitFormState = (
  form: FormInstance<any>,
  isEdit?: boolean,
  requiredFields?: Array<string | string[]>,
  nameChild?: string,
  childRequiredFields?: Array<string>,
  listChild?: any,
) => {
  const [buttonState, setButtonState] = useState("default");
  const [fieldState, setFieldState] = useState<Record<string, FieldState>>({});
  const [fieldChildState, setFieldChildState] = useState<
    Record<string, FieldState>
  >({});
  const [submitClicked, setSubmitClicked] = useState(false);
  const userChangedRef = useRef(false);

  const onValuesChange = () => {
    userChangedRef.current = true;
    checkState();
  };

  const checkState = (fromSubmit = false) => {
    const values = form.getFieldsValue(true);

    const hasChanged = userChangedRef.current;

    let hasMissingRequired = false;
    const nextFieldState: Record<string, FieldState> = {};
    const nextFieldChildState: Record<string, FieldState> = {};

    requiredFields?.forEach((name) => {
      const fieldKey = getFieldKey(name);
      const v = getFieldValue(values, name);

      const hasValue =
        v !== null &&
        v !== undefined &&
        !(typeof v === "string" && v.trim() === "") &&
        !(Array.isArray(v) && v.length === 0);

      if (isEdit && !submitClicked && !fromSubmit && !hasChanged) {
        nextFieldState[fieldKey] = "normal";
        return;
      }

      if (!hasValue) {
        hasMissingRequired = true;
        nextFieldState[fieldKey] =
          submitClicked || fromSubmit ? "error" : "initial";
      } else {
        nextFieldState[fieldKey] = "normal";
      }
    });
    setFieldState(nextFieldState);

    let hasMissingChildRequired = false;
    if (nameChild) {
      const valuesChild = values[nameChild];

      if (valuesChild && typeof valuesChild === "object") {
        const list = Object.values(valuesChild);
        list?.forEach((item: any, index) => {
          childRequiredFields?.forEach((name) => {
            const v = item?.[name];

            const hasValue =
              v !== "" &&
              v !== null &&
              v !== undefined &&
              !(Array.isArray(v) && v.length === 0);

            if (isEdit && !submitClicked && !fromSubmit && !hasChanged) {
              nextFieldChildState[`${name}-${index}`] = "normal";
              return;
            }
            if (!hasValue) {
              hasMissingChildRequired = true;
              nextFieldChildState[`${name}-${index}`] =
                submitClicked || fromSubmit ? "error" : "initial";
            } else {
              nextFieldChildState[`${name}-${index}`] = "normal";
            }
          });
        });
        setFieldChildState(nextFieldChildState);
      }
    }

    if (!isEdit) {
      if (hasMissingRequired || hasMissingChildRequired)
        return setButtonState("error");
      return setButtonState("success");
    }

    if (isEdit && !hasChanged) return setButtonState("default");

    if (hasMissingRequired || hasMissingChildRequired)
      return setButtonState("error");
    setButtonState("success");
  };

  useEffect(() => {
    setTimeout(checkState, 50);
  }, [listChild]);
  return {
    buttonState,
    getButtonStyle: (skipCheckChanged?: boolean) => ({
      background:
        buttonState === "default" && !skipCheckChanged
          ? MENU_COLORS.DISABLED_BUTTON
          : buttonState === "error"
          ? "#ffccff"
          : "#174a83",
      color: "white",
    }),
    fieldState,
    onValuesChange,
    getFieldClassName: (name: string | string[]) => {
      const fieldKey = getFieldKey(name);
      const state = fieldState[fieldKey];
      if (state === "initial") return "field-required-initial";
      if (state === "error") return "field-required-error";
      return "";
    },
    fieldChildState,
    getFieldChildClassName: (name: string, index: number) => {
      const state = fieldChildState[`${name}-${index}`];
      if (state === "initial") return "field-required-initial";
      if (state === "error") return "field-required-error";
      return "";
    },
    registerFormWatcher: () => ({
      onValuesChange,
    }),
    markSubmit: () => {
      setSubmitClicked(true);
      checkState(true);
    },
    checkState,
    unMarkSubmit: () => {
      setSubmitClicked(false);
      checkState(false);
      userChangedRef.current = false;
    },
  };
};

export const useSubmitFormStateFormik = (
  formik: any,
  isEdit?: boolean,
  requiredFields?: Array<string>,
) => {
  const [buttonState, setButtonState] = useState("default");
  const [fieldState, setFieldState] = useState<Record<string, FieldState>>({});
  const initialValuesRef = useRef<any>(null);

  useEffect(() => {
    initialValuesRef.current = JSON.parse(JSON.stringify(formik.values));
  }, []);

  const checkState = (fromSubmit = false) => {
    const values = formik.values;

    const hasChanged =
      initialValuesRef.current && !isEqual(initialValuesRef.current, values);

    let hasMissingRequired = false;
    const nextFieldState: Record<string, FieldState> = {};

    requiredFields?.forEach((name) => {
      const v = values[name];

      const hasValue =
        v !== "" &&
        v !== null &&
        v !== undefined &&
        !(Array.isArray(v) && v.length === 0);

      if (isEdit && !fromSubmit && !hasChanged) {
        nextFieldState[name] = "normal";
        return;
      }
      if (!hasValue) {
        hasMissingRequired = true;
        nextFieldState[name] = fromSubmit ? "error" : "initial";
      } else {
        nextFieldState[name] = "normal";
      }
    });
    setFieldState(nextFieldState);

    if (!isEdit) {
      if (hasMissingRequired) return setButtonState("error");
      return setButtonState("success");
    }

    if (isEdit && !hasChanged) return setButtonState("default");

    if (hasMissingRequired) return setButtonState("error");

    setButtonState("success");
  };

  useEffect(() => {
    checkState();
  }, [formik.values]);

  return {
    buttonState,
    getButtonStyle: () => ({
      background:
        buttonState === "default"
          ? MENU_COLORS.DISABLED_BUTTON
          : buttonState === "error"
          ? "#ffccff"
          : "#174a83",
      color: "white",
      borderColor: buttonState === "error" ? "#ffccff" : undefined,
    }),
    fieldState,
    getFieldClassName: (name: string) => {
      const state = fieldState[name];
      if (state === "initial") return "field-required-initial";
      if (state === "error") return "field-required-error";
      return "";
    },
    registerFormWatcher: () => ({
      onValuesChange: () => {
        checkState();
      },
    }),
    markSubmit: () => {
      checkState(true);
      const touched: Record<string, boolean> = {};
      requiredFields?.forEach((field) => {
        touched[field] = true;
      });
      formik.setTouched(touched, true);
    },
  };
};
